import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InternationalMarketing = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState({});
  const [showTokenForm, setShowTokenForm] = useState(null);
  const [tokenForm, setTokenForm] = useState({
    platform: '',
    access_token: '',
    account_id: '',
    account_name: ''
  });
  const [loading, setLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [campaignForm, setCampaignForm] = useState({
    product_name: '',
    product_description: '', 
    affiliate_link: '',
    target_regions: [],
    platforms: [],
    campaign_duration: '30',
    budget_per_region: 0
  });

  const regions = {
    "US": {
      name: "Estados Unidos",
      flag: "🇺🇸",
      language: "English (US)",
      currency: "USD",
      timezone: "EST/PST",
      market_size: "330M pessoas",
      avg_income: "$65,000",
      top_platforms: ["Facebook", "Instagram", "YouTube", "TikTok"],
      best_niches: ["Tech", "Business", "Health", "Lifestyle"]
    },
    "CA": {
      name: "Canadá", 
      flag: "🇨🇦",
      language: "English/French",
      currency: "CAD",
      timezone: "EST/PST",
      market_size: "38M pessoas",
      avg_income: "$55,000 CAD",
      top_platforms: ["Facebook", "Instagram", "YouTube"],
      best_niches: ["Outdoor", "Tech", "Health", "Finance"]
    },
    "GB": {
      name: "Reino Unido",
      flag: "🇬🇧", 
      language: "English (UK)",
      currency: "GBP",
      timezone: "GMT",
      market_size: "67M pessoas",
      avg_income: "£35,000",
      top_platforms: ["Facebook", "Instagram", "YouTube"],
      best_niches: ["Finance", "Tech", "Fashion", "Home"]
    },
    "AU": {
      name: "Austrália",
      flag: "🇦🇺",
      language: "English (AU)", 
      currency: "AUD",
      timezone: "AEST",
      market_size: "26M pessoas",
      avg_income: "$70,000 AUD",
      top_platforms: ["Facebook", "Instagram", "YouTube"],
      best_niches: ["Lifestyle", "Outdoor", "Tech", "Health"]
    },
    "DE": {
      name: "Alemanha",
      flag: "🇩🇪",
      language: "German",
      currency: "EUR", 
      timezone: "CET",
      market_size: "83M pessoas",
      avg_income: "€45,000",
      top_platforms: ["Facebook", "Instagram", "YouTube"],
      best_niches: ["Tech", "Automotive", "Business", "Quality"]
    },
    "MX": {
      name: "México",
      flag: "🇲🇽",
      language: "Spanish",
      currency: "MXN",
      timezone: "CST", 
      market_size: "128M pessoas",
      avg_income: "$15,000",
      top_platforms: ["Facebook", "Instagram", "YouTube", "TikTok"],
      best_niches: ["Fashion", "Tech", "Home", "Lifestyle"]
    },
    "ES": {
      name: "Espanha",
      flag: "🇪🇸",
      language: "Spanish",
      currency: "EUR",
      timezone: "CET", 
      market_size: "47M pessoas",
      avg_income: "€30,000",
      top_platforms: ["Facebook", "Instagram", "YouTube"],
      best_niches: ["Fashion", "Travel", "Food", "Lifestyle"]
    },
    "BR": {
      name: "Brasil",
      flag: "🇧🇷",
      language: "Portuguese",
      currency: "BRL",
      timezone: "BRT", 
      market_size: "213M pessoas",
      avg_income: "R$35,000",
      top_platforms: ["Facebook", "Instagram", "YouTube", "TikTok"],
      best_niches: ["Tech", "Fashion", "Health", "Entertainment"]
    }
  };

  // Guias de como obter tokens para cada plataforma
  const platformGuides = {
    facebook: {
      title: 'Facebook Business',
      steps: [
        '1. Acesse developers.facebook.com',
        '2. Crie um App no Meta for Developers',
        '3. Vá em Tools > Graph API Explorer',
        '4. Gere um Token de Acesso de Página',
        '5. Copie o Page ID e Access Token'
      ],
      links: {
        main: 'https://developers.facebook.com/',
        docs: 'https://developers.facebook.com/docs/pages/getting-started'
      }
    },
    instagram: {
      title: 'Instagram Business',
      steps: [
        '1. Tenha um perfil Instagram Business',
        '2. Vincule ao Facebook Business',
        '3. Acesse developers.facebook.com',
        '4. Use a Instagram Graph API',
        '5. Gere token com permissões do Instagram'
      ],
      links: {
        main: 'https://developers.facebook.com/docs/instagram-api/',
        docs: 'https://developers.facebook.com/docs/instagram-api/getting-started'
      }
    },
    youtube: {
      title: 'YouTube Data API',
      steps: [
        '1. Acesse console.cloud.google.com',
        '2. Crie um projeto e ative YouTube Data API v3',
        '3. Crie credenciais OAuth 2.0',
        '4. Autorize com sua conta do YouTube',
        '5. Copie o Access Token gerado'
      ],
      links: {
        main: 'https://console.cloud.google.com/',
        docs: 'https://developers.google.com/youtube/v3/getting-started'
      }
    },
    tiktok: {
      title: 'TikTok for Developers',
      steps: [
        '1. Acesse developers.tiktok.com',
        '2. Crie um App de desenvolvedor',
        '3. Solicite acesso à Content Posting API',
        '4. Configure OAuth para seu app',
        '5. Gere e copie o Access Token'
      ],
      links: {
        main: 'https://developers.tiktok.com/',
        docs: 'https://developers.tiktok.com/doc/content-posting-api-get-started'
      }
    },
    twitter: {
      title: 'Twitter/X API',
      steps: [
        '1. Acesse developer.twitter.com',
        '2. Crie um projeto e app',
        '3. Gere API Keys e Access Tokens',
        '4. Configure permissões de escrita',
        '5. Copie os tokens gerados'
      ],
      links: {
        main: 'https://developer.twitter.com/',
        docs: 'https://developer.twitter.com/en/docs/twitter-api/getting-started'
      }
    },
    linkedin: {
      title: 'LinkedIn Marketing API',
      steps: [
        '1. Acesse linkedin.com/developers',
        '2. Crie um App de desenvolvedor',
        '3. Solicite acesso ao Marketing API',
        '4. Configure OAuth 2.0',
        '5. Gere Access Token'
      ],
      links: {
        main: 'https://www.linkedin.com/developers/',
        docs: 'https://learn.microsoft.com/en-us/linkedin/marketing/'
      }
    }
  };

  useEffect(() => {
    fetchInternationalData();
    fetchSocialAccounts();
  }, []);

  const fetchInternationalData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/international/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to fetch international data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialAccounts = async () => {
    try {
      const response = await axios.get(`${API}/social/accounts`);
      setSocialAccounts(response.data.social_accounts || {});
    } catch (error) {
      console.error('Failed to fetch social accounts:', error);
    }
  };

  const openConnectForm = (platform) => {
    setTokenForm({
      platform: platform,
      access_token: '',
      account_id: '',
      account_name: ''
    });
    setShowTokenForm(platform);
    setMessage('');
    setError('');
  };

  const connectAccount = async (e) => {
    e.preventDefault();
    setConnectLoading(true);
    setError('');
    setMessage('');

    try {
      await axios.post(`${API}/social/connect`, {
        platform: tokenForm.platform,
        access_token: tokenForm.access_token,
        account_id: tokenForm.account_id,
        account_name: tokenForm.account_name
      });

      setMessage(`${tokenForm.platform.toUpperCase()} conectado com sucesso! ✅`);
      setShowTokenForm(null);
      setTokenForm({ platform: '', access_token: '', account_id: '', account_name: '' });
      
      fetchSocialAccounts();
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao conectar conta');
    } finally {
      setConnectLoading(false);
    }
  };

  const createInternationalCampaign = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/international/create-campaign`, {
        product_name: campaignForm.product_name,
        product_description: campaignForm.product_description,
        affiliate_link: campaignForm.affiliate_link,
        target_regions: campaignForm.target_regions,
        platforms: campaignForm.platforms,
        campaign_duration: parseInt(campaignForm.campaign_duration),
        auto_translate: true,
        schedule_by_timezone: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ Campanha internacional criada!\n\nPaíses: ${campaignForm.target_regions.map(r => regions[r]?.name || r).join(', ')}\n\nPlataformas: ${campaignForm.platforms.join(', ')}\n\nPosts serão publicados automaticamente no horário local de cada país!`);
      
      setShowCreateCampaign(false);
      setCampaignForm({
        product_name: '',
        product_description: '',
        affiliate_link: '',
        target_regions: [],
        platforms: [],
        campaign_duration: '30',
        budget_per_region: 0
      });
      
      fetchInternationalData();
    } catch (error) {
      console.error('Failed to create international campaign:', error);
      alert('Erro ao criar campanha internacional. Verifique as configurações.');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha internacional?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/international/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInternationalData();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Erro ao excluir campanha.');
    }
  };

  const handleRegionToggle = (regionCode) => {
    const updatedRegions = campaignForm.target_regions.includes(regionCode)
      ? campaignForm.target_regions.filter(r => r !== regionCode)
      : [...campaignForm.target_regions, regionCode];
    
    setCampaignForm({ ...campaignForm, target_regions: updatedRegions });
  };

  const handlePlatformToggle = (platform) => {
    const updatedPlatforms = campaignForm.platforms.includes(platform)
      ? campaignForm.platforms.filter(p => p !== platform)
      : [...campaignForm.platforms, platform];
    
    setCampaignForm({ ...campaignForm, platforms: updatedPlatforms });
  };

  const isConnected = (platform) => {
    return socialAccounts[platform] && (socialAccounts[platform].account_id || socialAccounts[platform].verified);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🌍 Marketing Internacional</h1>
        <p className="text-gray-600">Automatize suas campanhas para mercados globais de alta renda</p>
      </div>

      {/* Mensagens */}
      {message && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          {error}
        </div>
      )}

      {/* Status das Conexões Sociais - CONECTAR APIs */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">📱 Conexões de Redes Sociais</h2>
        <p className="text-sm text-gray-500 mb-4">Conecte suas contas de redes sociais para permitir postagens internacionais automatizadas.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { key: 'facebook', name: 'Facebook', icon: '👥', regions: 8 },
            { key: 'instagram', name: 'Instagram', icon: '📸', regions: 8 },
            { key: 'youtube', name: 'YouTube', icon: '🎥', regions: 8 },
            { key: 'tiktok', name: 'TikTok', icon: '🎵', regions: 6 },
            { key: 'twitter', name: 'Twitter/X', icon: '🐦', regions: 8 },
            { key: 'linkedin', name: 'LinkedIn', icon: '💼', regions: 8 }
          ].map(platform => (
            <div key={platform.key} className={`border-2 rounded-lg p-4 ${
              isConnected(platform.key) ? 'border-green-500 bg-green-50' : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{platform.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{platform.name}</h3>
                    <span className="text-xs text-gray-500">{platform.regions} mercados</span>
                  </div>
                </div>
                {isConnected(platform.key) && (
                  <span className="text-green-500 text-xl">✅</span>
                )}
              </div>
              
              {isConnected(platform.key) ? (
                <div className="text-sm text-green-700 bg-green-100 rounded p-2">
                  <strong>Conectado:</strong> {socialAccounts[platform.key].account_name || 'Conta ativa'}
                </div>
              ) : (
                <button
                  onClick={() => openConnectForm(platform.key)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
                >
                  🔗 Conectar {platform.name}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Conexão de Rede Social */}
      {showTokenForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                🔗 Conectar {platformGuides[showTokenForm]?.title || showTokenForm.toUpperCase()}
              </h3>
              <button 
                onClick={() => setShowTokenForm(null)} 
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>
            
            {/* Guia de como obter tokens */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-800 mb-2">📖 Como obter seu token:</h4>
              <ol className="text-sm text-blue-700 space-y-1">
                {platformGuides[showTokenForm]?.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              {platformGuides[showTokenForm]?.links && (
                <div className="mt-3 flex gap-3">
                  <a 
                    href={platformGuides[showTokenForm].links.main} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm font-medium"
                  >
                    🔗 Portal de Desenvolvedores
                  </a>
                  <a 
                    href={platformGuides[showTokenForm].links.docs} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm font-medium"
                  >
                    📚 Documentação
                  </a>
                </div>
              )}
            </div>

            <form onSubmit={connectAccount} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token *
                </label>
                <input
                  type="text"
                  value={tokenForm.access_token}
                  onChange={(e) => setTokenForm({...tokenForm, access_token: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Cole seu access token aqui"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Token de acesso da API (obrigatório)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID da Conta/Página
                </label>
                <input
                  type="text"
                  value={tokenForm.account_id}
                  onChange={(e) => setTokenForm({...tokenForm, account_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 123456789 ou @username"
                />
                <p className="text-xs text-gray-500 mt-1">ID numérico ou username da conta</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome de Identificação
                </label>
                <input
                  type="text"
                  value={tokenForm.account_name}
                  onChange={(e) => setTokenForm({...tokenForm, account_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Minha Página de Negócios"
                />
                <p className="text-xs text-gray-500 mt-1">Nome para identificar esta conexão</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={connectLoading || !tokenForm.access_token}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded-lg"
                >
                  {connectLoading ? '⏳ Conectando...' : '✅ Conectar Conta'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowTokenForm(null)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mercados Disponíveis */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🌍 Mercados Globais Disponíveis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(regions).map(([code, region]) => (
            <div key={code} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">{region.flag}</span>
                  <h3 className="font-semibold text-gray-900">{region.name}</h3>
                </div>
                <span className="text-sm font-medium text-blue-600">{region.currency}</span>
              </div>
              
              <div className="space-y-1 text-xs text-gray-600">
                <p><strong>População:</strong> {region.market_size}</p>
                <p><strong>Renda:</strong> {region.avg_income}</p>
                <p><strong>Timezone:</strong> {region.timezone}</p>
              </div>
              
              <div className="mt-2">
                <div className="flex flex-wrap gap-1">
                  {region.best_niches.slice(0, 2).map(niche => (
                    <span key={niche} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {niche}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Criar Campanha Internacional */}
      <div className="mb-8">
        <button
          onClick={() => setShowCreateCampaign(true)}
          className="bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg"
        >
          🌍 Criar Campanha Internacional
        </button>
      </div>

      {/* Modal de Criação de Campanha Internacional */}
      {showCreateCampaign && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">🌍 Criar Campanha Internacional</h3>
              <button onClick={() => setShowCreateCampaign(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[70vh] overflow-y-auto">
              {/* Informações do Produto */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-700">📦 Informações do Produto</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Produto *</label>
                  <input
                    type="text"
                    value={campaignForm.product_name}
                    onChange={(e) => setCampaignForm({...campaignForm, product_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Ex: Curso de Marketing Digital"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea
                    rows={2}
                    value={campaignForm.product_description}
                    onChange={(e) => setCampaignForm({...campaignForm, product_description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Descreva os benefícios..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link de Afiliado *</label>
                  <input
                    type="url"
                    value={campaignForm.affiliate_link}
                    onChange={(e) => setCampaignForm({...campaignForm, affiliate_link: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duração (dias)</label>
                  <select
                    value={campaignForm.campaign_duration}
                    onChange={(e) => setCampaignForm({...campaignForm, campaign_duration: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="7">7 dias</option>
                    <option value="14">14 dias</option>
                    <option value="30">30 dias</option>
                    <option value="60">60 dias</option>
                    <option value="90">90 dias</option>
                  </select>
                </div>
              </div>

              {/* Países e Plataformas */}
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-700">🌍 Países Alvo *</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(regions).map(([code, region]) => (
                    <button
                      key={code}
                      onClick={() => handleRegionToggle(code)}
                      className={`p-2 rounded-lg border-2 text-left ${
                        campaignForm.target_regions.includes(code)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <span className="text-lg">{region.flag}</span>
                      <span className="text-sm ml-1">{region.name}</span>
                    </button>
                  ))}
                </div>

                <h4 className="font-semibold text-gray-700 mt-4">📱 Plataformas *</h4>
                <div className="grid grid-cols-3 gap-2">
                  {['facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin'].map(platform => (
                    <button
                      key={platform}
                      onClick={() => handlePlatformToggle(platform)}
                      className={`p-2 rounded-lg border-2 text-center ${
                        campaignForm.platforms.includes(platform)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      } ${!isConnected(platform) ? 'opacity-50' : ''}`}
                    >
                      <div className="text-sm capitalize">{platform}</div>
                      {isConnected(platform) ? (
                        <span className="text-xs text-green-600">✅</span>
                      ) : (
                        <span className="text-xs text-red-500">❌</span>
                      )}
                    </button>
                  ))}
                </div>
                {campaignForm.platforms.some(p => !isConnected(p)) && (
                  <p className="text-xs text-orange-600">⚠️ Algumas plataformas não estão conectadas. Conecte-as acima antes de criar a campanha.</p>
                )}
              </div>
            </div>

            {/* Resumo */}
            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">📋 Resumo da Campanha</h4>
              <div className="text-sm text-purple-700">
                <p>• <strong>Países:</strong> {campaignForm.target_regions.map(r => regions[r]?.name).join(', ') || 'Nenhum'}</p>
                <p>• <strong>Plataformas:</strong> {campaignForm.platforms.join(', ') || 'Nenhuma'}</p>
                <p>• <strong>Duração:</strong> {campaignForm.campaign_duration} dias</p>
                <p>• <strong>✨ Tradução automática</strong> para cada idioma local</p>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={createInternationalCampaign}
                disabled={!campaignForm.product_name || !campaignForm.affiliate_link || campaignForm.target_regions.length === 0 || campaignForm.platforms.length === 0}
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-2 px-4 rounded-lg"
              >
                🚀 Criar Campanha Internacional
              </button>
              <button
                onClick={() => setShowCreateCampaign(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campanhas Internacionais Ativas */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-blue-50">
          <h2 className="text-xl font-semibold text-gray-900">📊 Campanhas Internacionais Ativas ({campaigns.length})</h2>
        </div>
        
        {campaigns.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">🌍</div>
            <p>Nenhuma campanha internacional ativa.</p>
            <p className="text-sm">Crie sua primeira campanha para alcançar mercados globais!</p>
          </div>
        ) : (
          <div className="divide-y">
            {campaigns.map(campaign => (
              <div key={campaign.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{campaign.product_name}</h3>
                    <p className="text-sm text-gray-500">
                      Países: {campaign.target_regions?.map(r => regions[r]?.flag || r).join(' ')} | 
                      Plataformas: {campaign.platforms?.join(', ')}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        campaign.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status === 'active' ? '🟢 Ativa' : '⏸️ Pausada'}
                      </span>
                      {campaign.auto_translate && (
                        <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">
                          🔄 Tradução Auto
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteCampaign(campaign.id)}
                    className="text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 rounded hover:bg-red-50"
                  >
                    🗑️ Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InternationalMarketing;
